// ./src/components/layouts/DashboardLayout/DesktopSidebar.tsx
import React, { useCallback, useState } from "react";
import {
  Box,
  Group,
  Text,
  NavLink,
  ScrollArea,
  Stack,
  Divider,
  Avatar,
  Menu,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { SiteMapItem, preloadRoute, splitShowMore, activeNavItem } from "@/config";
import { useLayoutUI } from "@/contexts/LayoutUIContext";
import { matchCurrentEventId } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { User } from "@/utils/api/UserService";

import { Logo } from "@/components/generic/Logo";
import { Badge } from "@mantine/core";
import { useOptionalEmNotificationsContext } from "@/contexts/EmNotificationsContext";
import { getRoutePath } from "@/config/siteMap";
import { AppIcon } from "@/components/generic/AppIcon";

import { DEFAULT_ICON_COLOR, HEADER_HEIGHT } from "./constants";

// ============================================
// Types
// ============================================

interface DesktopSidebarProps {
  navigationItems: SiteMapItem[];
  user: User | null | undefined;
  userInitials: string;
  appVersion: string;
  currentPath: string;
  onNavClick: (path: string) => void;
  onToggleCollapse: () => void;
  onSignOut: () => void;
  onOpenSupportDrawer: () => void;
  getTranslatedName: (translationKey?: string, fallback?: string) => string;
  isItemActive: (item: SiteMapItem) => boolean;
  t: (key: string) => string;
}

// ============================================
// Component
// ============================================

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  navigationItems,
  user,
  userInitials,
  appVersion,
  currentPath,
  onNavClick,
  onSignOut,
  onOpenSupportDrawer,
  getTranslatedName,
  isItemActive,
  t,
}) => {
  // A2b #57 — the badge. ⚠ NULL means "no provider", which is a real state (a password-crew session
  // has no account user and no bell), not an error — the sidebar must render fine without one.
  const notifications = useOptionalEmNotificationsContext();
  const notificationsUnread = notifications ? notifications.unread ?? 0 : null;

  const theme = useMantineTheme();
  const { isSectionExpanded, setSectionExpanded } = useLayoutUI();
  const [userMenuOpened, { toggle: toggleUserMenu, close: closeUserMenu }] =
    useDisclosure();

  // ============================================
  // Menu Handlers
  // ============================================

  const handleMenuAction = useCallback(
    (path: string) => {
      onNavClick(path);
      closeUserMenu();
    },
    [onNavClick, closeUserMenu]
  );

  const handleSignOut = useCallback(() => {
    closeUserMenu();
    onSignOut();
  }, [closeUserMenu, onSignOut]);

  // ============================================
  // "Show more" (ROADMAP A2b #63)
  // ============================================

  /**
   * Which "Show more" rows are open — PER EVENT, for this session.
   * ⚠⚠ NOT in `LayoutUIContext` beside the sections' state, deliberately. That is persisted and keyed by section name,
   * so one click on one event would open "Show more" on EVERY event, for good — quietly undoing the tidy menu the whole
   * feature is for (found in review, 2026-09-29). Component state survives navigation (the shell is hoisted, #58) and
   * starts closed on the next visit.
   */
  const [moreOpen, setMoreOpen] = useState<Record<string, boolean>>({});
  const moreScope = matchCurrentEventId(currentPath) ?? "account";
  const isMoreOpen = (id: string): boolean => !!moreOpen[`${moreScope}|${id}`];
  const toggleMore = (id: string) =>
    setMoreOpen((prev) => ({ ...prev, [`${moreScope}|${id}`]: !prev[`${moreScope}|${id}`] }));

  /**
   * The row that opens and closes what an event does not use. ⚠ The COUNT is in the label: "Show more" alone reads as
   * a control that might reveal nothing, and hides how much is there.
   */
  /**
   * ⚠ IT MUST NOT LOOK LIKE A PAGE (owner, 2026-09-29: "Show less is hard to spot — it looks exactly like the other menu
   * items"). A dashed rule above it marks where the pages the event uses end, and the label + chevron are in the BRAND
   * colour, which no page row uses unless it is the current one.
   */
  const renderShowMore = (id: string, count: number, open: boolean, depth: number) => (
    <NavLink
      key={`more-${id}`}
      label={
        open
          ? t("navigation.showLess")
          : `${t("navigation.showMore")} (${count})`
      }
      leftSection={
        <AppIcon
          iconName={open ? "ChevronUp" : "ChevronDown"}
          size={depth > 0 ? 20 : 26}
          color={theme.colors.brand[6]}
        />
      }
      aria-expanded={open}
      onClick={() => toggleMore(id)}
      variant="subtle"
      styles={{
        root: {
          borderRadius: theme.radius.md,
          marginBottom: 2,
          marginTop: 4,
          borderTop: `1px dashed ${theme.colors.neutral[3]}`,
        },
        label: { fontWeight: 600, fontSize: depth > 0 ? 15 : 16, color: theme.colors.brand[6] },
      }}
    />
  );

  /**
   * Items as the menu shows them: what the event uses, then "Show more"; open, the rest in a TINTED block with
   * "Show less" UNDER it — where the eye ends up after reading the extras, and visibly the end of that group.
   * ⚠⚠ THE PAGE THE HOST IS ON IS NEVER TUCKED AWAY. Closed, "Show more" still lists the current page if it is one of
   * its own, so opening a page from it (or from a link, or ⌘K) never leaves the menu with nothing lit.
   */
  const renderWithShowMore = (items: SiteMapItem[], id: string, depth: number) => {
    const { shown, more } = splitShowMore(items);

    if (more.length === 0) {
      return shown.map((item, index) => renderNavItem(item, index, depth));
    }

    const active = activeNavItem(items, currentPath);
    const open = isMoreOpen(id);

    if (!open) {
      const revealed = more.filter((item) => item === active);

      return [
        ...shown.map((item, index) => renderNavItem(item, index, depth)),
        renderShowMore(id, more.length, open, depth),
        ...revealed.map((item, index) => renderNavItem(item, shown.length + index, depth)),
      ];
    }

    return [
      ...shown.map((item, index) => renderNavItem(item, index, depth)),
      <Box
        key={`more-group-${id}`}
        mt={4}
        p={2}
        style={{ backgroundColor: theme.colors.neutral[1], borderRadius: theme.radius.md }}
      >
        {more.map((item, index) => renderNavItem(item, shown.length + index, depth))}
      </Box>,
      renderShowMore(id, more.length, open, depth),
    ];
  };

  /** ⚠ The premium mark (ROADMAP #63: premium is SHOWN, never hidden). Opening the page shows its upgrade screen. */
  const premiumMark = (item: SiteMapItem) =>
    item.navPremium ? (
      <Box component="span" role="img" aria-label={t("navigation.premium")} style={{ display: "flex" }}>
        <AppIcon iconName="Crown" size={16} color={theme.colors.yellow[6]} />
      </Box>
    ) : undefined;

  // ============================================
  // Navigation Renderer
  // ============================================

  const renderNavItem = (item: SiteMapItem, index: number, depth = 0) => {
    const hasChildren = Array.isArray(item.items) && item.items.length > 0;
    const translatedItemName = getTranslatedName(
      item.options?.translationKey,
      item.name
    );
    const itemIconColor = item.iconColor || DEFAULT_ICON_COLOR;
    const isChild = depth > 0;
    const iconSize = isChild ? 20 : 26;
    const isActive = item.url
      ? currentPath === item.url
      : isItemActive(item);

    // Child rows read as nested: smaller, muted icons + lighter label.
    const labelStyles = {
      fontWeight: isChild ? 400 : 500,
      fontSize: isChild ? 15 : 16,
      // Only tint inactive children grey; active rows keep the coral from
      // the "light" variant.
      color: isChild ? theme.colors.neutral[6] : undefined,
    };

    if (hasChildren) {
      // ⚠⚠ CONTROLLED, NOT `defaultOpened`. `defaultOpened` is read ONCE, at mount — and this whole
      // sidebar is remounted on every cross-page navigation, because `DashboardLayout` lives inside
      // each page component (see LayoutUIContext's header, and ROADMAP A2b #58). So every group the
      // host had opened collapsed the moment they went anywhere, and no amount of speed would have
      // fixed it: the state simply did not exist any more.
      // ▶ The opinion now lives in `LayoutUIContext`, ABOVE the routes, so it outlives the shell.
      // `isActive` is still the default for a group nobody has touched — it holds the current page.
      const sectionId = `${item.scope ?? "account"}:${item.key ?? item.name}`;
      const opened = isSectionExpanded(sectionId, isActive);

      return (
        <NavLink
          key={`parent-${item.name}-${index}`}
          active={isActive}
          color="brand"
          label={translatedItemName}
          leftSection={
            <AppIcon
              iconName={item.icon || "Dashboard"}
              size={iconSize}
              color={isActive ? theme.colors.brand[6] : itemIconColor}
            />
          }
          childrenOffset={28}
          opened={opened}
          onChange={(next) => setSectionExpanded(sectionId, next)}
          variant="subtle"
          styles={{
            root: { borderRadius: theme.radius.md, marginBottom: 2 },
            label: labelStyles,
          }}
        >
          {renderWithShowMore(item.items!, `${sectionId}:more`, depth + 1)}
        </NavLink>
      );
    }

    if (item.url) {
      const active = currentPath === item.url;
      return (
        <NavLink
          key={`leaf-${item.url}-${index}`}
          active={active}
          // Native "light" active state → coral-tinted pill + coral label.
          color="brand"
          variant={active ? "light" : "subtle"}
          label={translatedItemName}
          leftSection={
            <AppIcon
              iconName={item.icon || "Dashboard"}
              size={iconSize}
              color={
                active
                  ? theme.colors.brand[6]
                  : isChild
                    ? theme.colors.neutral[5]
                    : itemIconColor
              }
            />
          }
          rightSection={premiumMark(item)}
          onClick={() => onNavClick(item.url!)}
          // ⚠ Start the page's chunk on hover. A mouse reaches a sidebar row a few hundred
          // milliseconds before it clicks, which is usually the whole download — so the first visit
          // to a page stops blanking the screen. Free when the chunk is already in memory.
          onPointerEnter={() => preloadRoute(item)}
          styles={{
            root: {
              borderRadius: theme.radius.md,
              marginBottom: 2,
              // Inset accent bar on the active row (plain inline style — no
              // pseudo-selectors, which Mantine's `styles` prop ignores).
              boxShadow: active
                ? `inset 3px 0 0 0 ${theme.colors.brand[6]}`
                : undefined,
            },
            label: {
              ...labelStyles,
              color: active
                ? theme.colors.brand[7]
                : isChild
                  ? theme.colors.neutral[6]
                  : undefined,
            },
          }}
        />
      );
    }

    return null;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Stack h="100%" gap={0} bg="neutral.0">
      {/* Desktop Logo Section — same height as the page header, white bg so
          the logo mark sits on a clean field and both bottom borders align. */}
      <Box
        px="lg"
        h={HEADER_HEIGHT}
        bg="white"
        style={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Logo size="small" withName version={appVersion} />
      </Box>

      <Divider color="neutral.2" />

      {/* Navigation */}
      <ScrollArea flex={1} p="xs" px="sm" scrollbarSize={6}>
        <Stack gap={2}>
          {renderWithShowMore(navigationItems, `${navigationItems.some((item) => item.scope === "event") ? "event" : "account"}:menu:more`, 0)}
        </Stack>
      </ScrollArea>

      <Divider color="neutral.2" />

      {/* Help & Support */}
      <Box px="sm" pb="xs">
        {/* A2b #57 — notifications. ⚠ The DESKTOP home for the bell: the phone header has one, and
            the sidebar is where a desktop user looks. `unread` comes from the app-wide provider, so
            both read the SAME count — two hooks would be two counts that disagree the moment either
            marked something read. Renders nothing without a provider (password crew have no bell). */}
        {notificationsUnread !== null && (
          <NavLink
            label={t("navigation.notifications")}
            leftSection={<AppIcon iconName="Bell" size={18} />}
            rightSection={
              notificationsUnread > 0 ? (
                <Badge size="sm" color="red" variant="filled" circle={notificationsUnread < 10}>
                  {notificationsUnread > 99 ? "99+" : notificationsUnread}
                </Badge>
              ) : undefined
            }
            onClick={() => onNavClick(getRoutePath("notifications"))}
            active={currentPath === "/notifications"}
            variant="subtle"
            styles={{
              root: { borderRadius: theme.radius.md, marginBottom: 2, marginTop: 3 },
              label: { fontWeight: 500, fontSize: 16 },
            }}
          />
        )}

        <NavLink
          label={t("navigation.helpAndSupport")}
          leftSection={<AppIcon iconName="Help" size={18} />}
          onClick={onOpenSupportDrawer}
          variant="subtle"
          styles={{
            root: {
              borderRadius: theme.radius.md,
              marginBottom: 2,
              marginTop: 3,
            },
            label: {
              fontWeight: 500,
              fontSize: 16,
            },
          }}
        />
      </Box>

      <Divider color="neutral.2" />

      {/* User Section */}
      <Box p="md">
        {user && (
          <Menu
            shadow="lg"
            width={260}
            position="top-start"
            opened={userMenuOpened}
            onChange={toggleUserMenu}
            withinPortal
            transitionProps={{ transition: "pop", duration: 150 }}
            styles={{
              label: {
                fontSize: 13,
                fontWeight: 600,
                paddingLeft: 12,
                paddingTop: 8,
                paddingBottom: 4,
              },
              item: {
                fontSize: 15,
                fontWeight: 500,
                padding: "12px 14px",
                borderRadius: theme.radius.sm,
                minHeight: 46,
              },
              itemSection: {
                marginRight: 12,
              },
              divider: {
                marginTop: 8,
                marginBottom: 8,
              },
            }}
          >
            <Menu.Target>
              <UnstyledButton
                w="100%"
                p="sm"
                px="md"
                style={{
                  borderRadius: theme.radius.md,
                  backgroundColor: userMenuOpened
                    ? theme.colors.neutral[1]
                    : undefined,
                  transition: "background-color 150ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme.colors.neutral[1];
                }}
                onMouseLeave={(e) => {
                  if (!userMenuOpened) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <Avatar size={36} color="brand" radius="md">
                    {userInitials}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="md" fw={600} truncate c="neutral.8" lh={1.3}>
                      {user.username}
                    </Text>
                    <Text size="md" truncate c="neutral.5" lh={1.3}>
                      {user.email}
                    </Text>
                  </Box>
                  <AppIcon
                    iconName="ChevronUp"
                    size={14}
                    color={theme.colors.neutral[4]}
                    style={{
                      transition: "transform 150ms ease",
                      transform: userMenuOpened
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                    }}
                  />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown
              style={{
                border: `1px solid ${theme.colors.neutral[2]}`,
                boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.15)",
              }}
              styles={{
                dropdown: {
                  padding: 8,
                },
              }}
            >
              {/* ⚠⚠ THIS MENU IS THE PERSON, NOT THE ACCOUNT — one home per destination.
                  It used to also offer Account Settings and Subscription, both of which are
                  ordinary, permanently visible sidebar entries (Settings → Account Settings,
                  Money → Subscription). Two doors to one room is not convenience: it makes a host
                  wonder whether the two are the same page, and it means a change of address has to
                  be made twice or the copies drift.
                  ▶ The split: the avatar answers "who am I, and how do I leave"; the sidebar
                  sections answer "what can I configure". Profile lives ONLY here, which is why its
                  sitemap entry is `visible: false`. Sign out lives only here too — the one thing
                  that must never be hunted for. Guarded by `npm run check-nav`. */}
              <Menu.Label>{t("pages.account.name")}</Menu.Label>
              <Menu.Item
                leftSection={<AppIcon iconName="User" size={20} />}
                onClick={() => handleMenuAction("/settings/profile")}
              >
                {t("pages.userProfilePage.name")}
              </Menu.Item>

              <Menu.Divider />
              <Menu.Item
                leftSection={<AppIcon iconName="Logout" size={20} />}
                color="error"
                onClick={handleSignOut}
              >
                {t("navigation.logout")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Box>
    </Stack>
  );
};